import { __makeTemplateObject, __spreadArray } from '../../node_modules/tslib/tslib.es6.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { media, SkipLink, StatusMessage } from '@elseu/sdu-titan';
import React, { useEffect } from 'react';
import styled, { createGlobalStyle, css } from 'styled-components';
import { findElementsForNavigation } from '../../utilities/findElementsForNavigation/findElementsForNavigation.js';
import { scrollToSection } from '../../utilities/scrollToSection/scrollToSection.js';
import { InPageNavigationProvider } from './InPageNavigationContext.js';

/**
 * Global styles for the application.
 */
var GlobalStyle = createGlobalStyle(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), function (_a) {
  var theme = _a.theme;
  return css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n    html {\n      scroll-behavior: smooth;\n    }\n\n    body {\n      color: #1e1741;\n      margin: 0;\n    }\n\n    * {\n      box-sizing: border-box;\n      text-rendering: optimizelegibility;\n      -webkit-font-smoothing: antialiased;\n    }\n\n    :root {\n      --breakpoint-xs: ", ";\n      --breakpoint-s: ", ";\n      --breakpoint-m: ", ";\n      --breakpoint-l: ", ";\n      --breakpoint-xl: ", ";\n      --breakpoint-xxl: ", ";\n    }\n  "], ["\n    html {\n      scroll-behavior: smooth;\n    }\n\n    body {\n      color: #1e1741;\n      margin: 0;\n    }\n\n    * {\n      box-sizing: border-box;\n      text-rendering: optimizelegibility;\n      -webkit-font-smoothing: antialiased;\n    }\n\n    :root {\n      --breakpoint-xs: ", ";\n      --breakpoint-s: ", ";\n      --breakpoint-m: ", ";\n      --breakpoint-l: ", ";\n      --breakpoint-xl: ", ";\n      --breakpoint-xxl: ", ";\n    }\n  "])), theme.breakpoints.xs, theme.breakpoints.s, theme.breakpoints.m, theme.breakpoints.l, theme.breakpoints.xl, theme.breakpoints.xxl);
});
var Main = styled.main.attrs({
  'aria-label': 'Main content'
})(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  padding-top: 65px;\n  min-height: calc(100vh - 548px + 65px);\n\n  ", "\n"], ["\n  padding-top: 65px;\n  min-height: calc(100vh - 548px + 65px);\n\n  ", "\n"])), media.greaterThan('s')(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n    padding-top: 129px;\n    min-height: calc(100vh - 129px);\n  "], ["\n    padding-top: 129px;\n    min-height: calc(100vh - 129px);\n  "]))));
/**
 * These sections are white by default, so we need to alternate the background color
 */
var whiteBackground = ['SectionForm', 'SectionContent', 'SectionNavigation'];
/**
 * Function to determine the next background color based on previous section's properties
 * @param {string} previousBackgroundColor - The previous section's background color.
 * @param {string} previousSectionType - The previous section's type.
 * @returns {string} - The next background color.
 */
var getNextBackgroundColor = function (previousBackgroundColor, previousSectionType) {
  if (whiteBackground.includes(previousSectionType)) {
    return 'sand';
  }
  return previousBackgroundColor === 'sand' ? 'white' : 'sand';
};
/**
 * Function to determine the background colors for each section
 * @param {React.ReactNode[]} sections - The array of sections.
 * @returns {string[]} - The array of background colors.
 */
var determineBackgroundColors = function (sections) {
  var headerBackground = React.isValidElement(sections[0]) ? sections[0].props.background : 'white';
  return sections.slice(1).reduce(function (backgroundColors, _, index) {
    return __spreadArray(__spreadArray([], backgroundColors, true), [getNextBackgroundColor(backgroundColors[index], sections[index].props.__typename)], false);
  }, [headerBackground]);
};
/**
 * useScrollToSection is a custom hook that scrolls to a specific section on the page
 * based on the hash in the URL. The hook adds an event listener to prevent the
 * browser's default scroll behavior on the first scroll event.
 */
var useScrollToSection = function () {
  useEffect(function () {
    var section = window.location.hash.substring(1);
    if (section) {
      var handleFirstScroll_1 = function (event) {
        event.preventDefault();
        window.removeEventListener('scroll', handleFirstScroll_1);
      };
      window.addEventListener('scroll', handleFirstScroll_1);
      if (document.readyState === 'complete') {
        scrollToSection(section);
      } else {
        window.addEventListener('load', function () {
          scrollToSection(section);
        });
      }
    }
  }, []);
};
/**
 * Page component to wrap and render sections with the appropriate background colors
 * @component
 * @param {PageProps} props - The properties for the Page component.
 * @returns {React.ReactNode} - The Page component.
 */
var Page = function (_a) {
  var seo = _a.seo,
    navigation = _a.navigation,
    footer = _a.footer,
    url = _a.url,
    toast = _a.toast,
    sectionsChildren = _a.sections,
    isPreview = _a.isPreview;
  var sections = React.Children.toArray(sectionsChildren);
  var backgroundColors = determineBackgroundColors(sections);
  var elementsForNavigation = findElementsForNavigation({
    elements: sections
  });
  useScrollToSection();
  return jsxs(InPageNavigationProvider, {
    elements: elementsForNavigation,
    children: [jsx(GlobalStyle, {}), seo, url && jsx(SkipLink, {
      label: "Naar de inhoud",
      target: "".concat(url, "#inhoud"),
      withPositioning: true
    }), navigation, jsxs(Main, {
      id: "inhoud",
      children: [isPreview && jsx(StatusMessage, {
        alignment: "center",
        link: {
          to: "/api/exit-preview?url=".concat(url)
        },
        children: "Je ziet nu de preview van deze pagina. Klik hier om de preview te verlaten."
      }), toast, sections.map(function (section, index) {
        return React.cloneElement(section, {
          background: backgroundColors[index],
          url: url
        });
      })]
    }), footer]
  });
};
var templateObject_1, templateObject_2, templateObject_3, templateObject_4;

export { GlobalStyle, Page };
//# sourceMappingURL=Page.js.map
